import { ComponentProps, useCallback, useEffect, useState } from 'react';
import FastValueViewer from 'components/ScalarSpreadsheet/utilities/FastValueViewer';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  COMMON_STOCK_VALUE,
  EQUITY_CONVERSION_MODEL_ROW,
  ISSUANCE_DATE_ALIAS,
  ISSUANCE_DATE_ROW,
  PREFERRED_STOCK_VALUE,
} from './config/constants';

type NoteCalculatedValueViewerProps = Omit<ComponentProps<typeof FastValueViewer>, 'cell'> & { cell: ICell };

const NoteCalculatedValueViewer = (props: NoteCalculatedValueViewerProps) => {
  const { cell, value } = props;
  const columns = (cell.totalKey
    ? Object.values(cell.sheet?.cells ?? {})
      .filter(date => date.alias === ISSUANCE_DATE_ALIAS && !date.totalKey && !date.isTitleCell && !date.isLegend)
      .map(date => date.columnLegend)
    : [cell.columnLegend]).join(',');

  const getDisplayState = useCallback(() => {
    const cells = cell.sheet?.cells ?? {};
    const liveCell = cells[cell.totalKey ? `TOTALS_${cell.totalKey}` : cell.key ?? ''] ?? cell;
    const rowNumber = liveCell.rowNumber ?? liveCell.key?.replace(/^[A-Z]+/, '');
    const unavailable = !liveCell.hidden && columns.split(',').some(column => {
      const date = cells[column + ISSUANCE_DATE_ROW];
      const model = Number(cells[column + EQUITY_CONVERSION_MODEL_ROW]?.value);
      const output = cells[column + rowNumber];
      return (model === PREFERRED_STOCK_VALUE || model === COMMON_STOCK_VALUE)
        && !output?.hidden && date?.isRequired && !String(date.value ?? '').trim();
    });
    return { hidden: Boolean(liveCell.hidden), unavailable, totalValue: cell.totalKey ? liveCell.value : undefined };
  }, [cell, columns]);
  const [displayState, setDisplayState] = useState(getDisplayState);

  // Date and model edits can change availability without changing the calculated number.
  useEffect(() => {
    const cells = cell.sheet?.cells ?? {};
    const watchedCells = columns.split(',').flatMap(column => [ISSUANCE_DATE_ROW, EQUITY_CONVERSION_MODEL_ROW]
      .map(row => cells[column + row]));
    if (cell.totalKey) {
      watchedCells.push(cells[`TOTALS_${cell.totalKey}`]);
    }
    const events = [...new Set(watchedCells.map(watchedCell => watchedCell?.getFullKey?.())
      .filter((event): event is string => Boolean(event)))];
    const updateAvailability = () => setDisplayState(getDisplayState());
    events.forEach(event => document.addEventListener(event, updateAvailability));
    updateAvailability();
    return () => events.forEach(event => document.removeEventListener(event, updateAvailability));
  }, [cell, columns, getDisplayState]);

  const { unavailable, hidden, totalValue } = displayState;
  // Total grid keys also identify the first note, so read the total's own workbook value.
  const displayValue = cell.totalKey ? totalValue : value;
  if (!unavailable && Boolean(cell.hidden) === hidden) {
    return <FastValueViewer {...props} value={displayValue} />;
  }

  const displayCell = Object.assign(Object.create(Object.getPrototypeOf(cell)), cell, { hidden }, unavailable ? {
    value: '',
    allowEmptyValues: true,
    tooltipMessages: [
      ...(cell.tooltipMessages ?? []),
      cell.totalKey
        ? 'Enter an Issuance Date for every note to calculate this total.'
        : 'Enter an Issuance Date to calculate this value.',
    ],
  } : {});
  return <FastValueViewer {...props} cell={displayCell} value={unavailable ? '' : displayValue} />;
};

export default NoteCalculatedValueViewer;
